import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// Hook to track element position relative to viewport
const useElementParallax = (ref) => {
  const [offset, setOffset] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const elementTop = rect.top;
        // Create parallax based on element's position in viewport
        setOffset(elementTop * -0.3);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call
    return () => window.removeEventListener('scroll', handleScroll);
  }, [ref]);
  
  return offset;
};

const PageHero = ({ 
  title, 
  subtitle, 
  breadcrumb,
  children,
  background = 'light' // 'light' or 'dark'
}) => {
  const heroRef = useRef(null);
  const offset = useElementParallax(heroRef);
  
  const bgClass = background === 'dark' 
    ? 'bg-[#1a1c1b] text-white'
    : 'bg-[#f4f5f3]';

  return (
    <section 
      ref={heroRef}
      className={`py-24 lg:py-32 relative overflow-hidden ${bgClass}`}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Large gradient blobs */}
        <div 
          className="absolute w-[500px] h-[500px] rounded-full opacity-40"
          style={{ 
            top: '-10%', 
            right: '-5%',
            background: background === 'dark' 
              ? 'radial-gradient(circle, rgba(255,0,0,0.3) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(255,0,0,0.15) 0%, transparent 70%)',
            transform: `translate(${offset * 0.15}px, ${offset * 0.1}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        <div 
          className="absolute w-[400px] h-[400px] rounded-full opacity-30"
          style={{ 
            bottom: '-15%', 
            left: '-10%',
            background: background === 'dark'
              ? 'radial-gradient(circle, rgba(255,100,100,0.2) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(255,200,200,0.5) 0%, transparent 70%)',
            transform: `translate(${offset * -0.1}px, ${offset * 0.15}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        
        {/* Decorative circles with parallax */}
        <div 
          className="absolute w-32 h-32 rounded-full border-2 hidden lg:block"
          style={{ 
            top: '15%', 
            left: '8%',
            borderColor: background === 'dark' ? 'rgba(255,0,0,0.3)' : 'rgba(255,0,0,0.2)',
            transform: `translateY(${offset * 0.25}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        <div 
          className="absolute w-20 h-20 rounded-full hidden lg:block"
          style={{ 
            top: '60%', 
            right: '12%',
            background: background === 'dark' ? 'rgba(255,0,0,0.15)' : 'rgba(255,0,0,0.1)',
            transform: `translateY(${offset * -0.2}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        <div 
          className="absolute w-16 h-16 rounded-full hidden xl:block"
          style={{ 
            bottom: '20%', 
            left: '15%',
            background: background === 'dark' ? 'rgba(255,100,100,0.2)' : 'rgba(255,200,200,0.4)',
            transform: `translateY(${offset * 0.18}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        
        {/* Diagonal lines */}
        <div 
          className="absolute w-64 h-0.5 hidden lg:block"
          style={{ 
            top: '30%', 
            right: '5%',
            background: background === 'dark' 
              ? 'linear-gradient(90deg, transparent, rgba(255,0,0,0.3), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(255,0,0,0.15), transparent)',
            transform: `rotate(-15deg) translateX(${offset * 0.1}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        <div 
          className="absolute w-48 h-0.5 hidden xl:block"
          style={{ 
            bottom: '35%', 
            left: '3%',
            background: background === 'dark'
              ? 'linear-gradient(90deg, transparent, rgba(255,0,0,0.25), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(255,0,0,0.1), transparent)',
            transform: `rotate(20deg) translateX(${offset * -0.08}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        
        {/* Arrow decorations */}
        <img 
          src="/arrow-up-right.webp" 
          alt="" 
          className="absolute w-24 h-auto hidden lg:block"
          style={{ 
            top: '20%', 
            left: '5%',
            opacity: background === 'dark' ? 0.2 : 0.15,
            transform: `translateY(${offset * 0.3}px) rotate(${offset * 0.02}deg)`,
            transition: 'transform 0.1s ease-out'
          }}
          aria-hidden="true"
        />
        <img 
          src="/arrow-down.webp" 
          alt="" 
          className="absolute w-20 h-auto hidden xl:block"
          style={{ 
            bottom: '25%', 
            right: '8%',
            opacity: background === 'dark' ? 0.15 : 0.1,
            transform: `translateY(${offset * -0.25}px)`,
            transition: 'transform 0.1s ease-out'
          }}
          aria-hidden="true"
        />
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Breadcrumb */}
        {breadcrumb && (
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link 
              to="/" 
              className={`hover:text-[#FF0000] transition-colors ${
                background === 'dark' ? 'text-gray-400' : 'text-[#5a5d5b]'
              }`}
            >
              Start
            </Link>
            <span className={background === 'dark' ? 'text-gray-600' : 'text-[#888b89]'}>/</span>
            <span className="text-[#FF0000]">{breadcrumb}</span>
          </nav>
        )}

        {/* Title */}
        <h1 className={`heading-1 mb-6 max-w-4xl ${background === 'dark' ? 'text-white' : ''}`}>
          {title}
        </h1>
        
        {/* Subtitle */}
        {subtitle && (
          <p className={`body-large mb-8 max-w-3xl ${background === 'dark' ? 'text-gray-300' : ''}`}>
            {subtitle}
          </p>
        )}
        
        {/* Additional content (buttons, etc.) */}
        {children}
      </div>
    </section>
  );
};

export default PageHero;
